const OPENMOJI_INDEX_URL =
  'https://raw.githubusercontent.com/hfg-gmuend/openmoji/master/data/openmoji.json';
const OPENMOJI_PNG_BASE =
  'https://raw.githubusercontent.com/hfg-gmuend/openmoji/master/color/618x618';

export interface OpenMojiEntry {
  hexcode: string;
  annotation: string;
  tags?: string;
  openmoji_tags?: string;
  group?: string;
  subgroups?: string;
}

let cachedOpenMoji: OpenMojiEntry[] | null = null;
let openMojiRequest: Promise<OpenMojiEntry[]> | null = null;

function isOpenMojiEntry(value: unknown): value is OpenMojiEntry {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.hexcode === 'string' && typeof candidate.annotation === 'string';
}

async function loadOpenMojiIndex(): Promise<OpenMojiEntry[]> {
  if (cachedOpenMoji) return cachedOpenMoji;
  if (openMojiRequest) return openMojiRequest;

  openMojiRequest = (async () => {
    const response = await fetch(OPENMOJI_INDEX_URL);
    if (!response.ok) {
      const responseBody = await response.text().catch(() => '');
      throw new Error(
        `OpenMoji request failed (${response.status}). ${responseBody.slice(0, 200)}`
      );
    }

    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) {
      cachedOpenMoji = [];
      return cachedOpenMoji;
    }

    cachedOpenMoji = payload.filter(isOpenMojiEntry);
    return cachedOpenMoji;
  })();

  try {
    return await openMojiRequest;
  } finally {
    openMojiRequest = null;
  }
}

function makeSearchText(entry: OpenMojiEntry): string {
  return [
    entry.annotation,
    entry.tags ?? '',
    entry.openmoji_tags ?? '',
    entry.group ?? '',
    entry.subgroups ?? '',
  ]
    .join(' ')
    .toLowerCase();
}

function scoreOpenMojiEntry(entry: OpenMojiEntry, terms: string[]): number {
  const haystack = makeSearchText(entry);
  let score = 0;
  for (const term of terms) {
    if (!term) continue;
    if (haystack.includes(term)) score += 1;
    if (entry.annotation.toLowerCase().includes(term)) score += 2;
  }
  return score;
}

export async function searchOpenMoji(
  query: string,
  options?: {
    limit?: number;
  }
): Promise<OpenMojiEntry[]> {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return [];

  const all = await loadOpenMojiIndex();
  const terms = trimmedQuery.split(/\s+/).filter(Boolean);
  const scored = all
    .map((entry) => ({
      entry,
      score: scoreOpenMojiEntry(entry, terms),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.entry.annotation.localeCompare(b.entry.annotation);
    });

  const limit = options?.limit ?? 40;
  return scored.slice(0, limit).map((item) => item.entry);
}

export function getOpenMojiPngUrl(entry: OpenMojiEntry): string {
  const hex = entry.hexcode.trim().toUpperCase();
  return `${OPENMOJI_PNG_BASE}/${hex}.png`;
}

export function generateOpenMojiAltText(entry: OpenMojiEntry, searchTerm?: string): string {
  const annotation = entry.annotation?.trim();
  if (annotation) return annotation;
  const fallback = searchTerm?.trim();
  return fallback ? `Emoji for ${fallback}` : 'Emoji from OpenMoji';
}

export function buildOpenMojiCredit(): string {
  return 'OpenMoji (CC BY-SA 4.0)';
}
